import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SwiperClass } from 'swiper/react';
import { useInView } from 'react-intersection-observer';
import 'swiper/css';
import 'swiper/css/pagination';

import {
  IAppFeatureAnimationFields,
  IAppFeatureAnimationItem
} from '../../../types/contentful';
import { getThemeClasses } from './themeConfig';
import {
  SwiperWithPaginationEvents,
  getBreakpoint,
  shouldReinitSwiper,
  setupPaginationClickHandler
} from './utils';
import { OldDesign } from './OldDesign';
import { NewDesign } from './NewDesign';

interface Props extends Omit<IAppFeatureAnimationFields, 'contentfulLabel'> {
  sysId: string;
}

export function AppFeatureAnimation({
  items,
  ctaWidget,
  sysId,
  redesignToggle = false,
  colorTheme = 'light',
  invertColors = false
}: Props) {
  const [videoSwiper, setVideoSwiper] = useState<SwiperClass | null>(null);
  const [labelsSwiper, setLabelsSwiper] = useState<SwiperClass | null>(null);
  const paginationRef = useRef<HTMLDivElement | null>(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);
  const [itemsState, setItemsState] = useState<Record<string, boolean>>(
    items.reduce((acc, item) => ({ ...acc, [item.fields.label]: false }), {})
  );
  const [inViewRef, inView] = useInView({ threshold: 0.25 });
  const [swiperReinitKey, setSwiperReinitKey] = useState(0);

  const goToIndex = useCallback(
    (index: number) => {
      setSelectedItemIndex(index);
      playItem(items[index], true);
      if (videoSwiper) {
        videoSwiper.slideTo(index, 700);
      }
      if (labelsSwiper) {
        labelsSwiper.slideTo(index, 700);
      }
    },
    [videoSwiper, labelsSwiper, items]
  );

  useEffect(() => {
    return setupPaginationClickHandler(paginationRef, videoSwiper, goToIndex);
  }, [videoSwiper, goToIndex]);

  useEffect(() => {
    if (!redesignToggle) return;

    let timeoutId: number | undefined;
    let lastBreakpoint = getBreakpoint(window.innerWidth);
    let lastWidth = window.innerWidth;

    const handleResize = () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        const currentBreakpoint = getBreakpoint(window.innerWidth);
        const currentWidth = window.innerWidth;

        if (
          shouldReinitSwiper(
            currentBreakpoint,
            lastBreakpoint,
            currentWidth,
            lastWidth
          )
        ) {
          if (redesignToggle) {
            // For new design, check if we're crossing desktop breakpoint
            const isCrossingDesktopBreakpoint =
              (lastBreakpoint === 'desktop' &&
                currentBreakpoint !== 'desktop') ||
              (lastBreakpoint !== 'desktop' && currentBreakpoint === 'desktop');

            if (isCrossingDesktopBreakpoint) {
              // Only reinitialize video swiper when crossing desktop breakpoint (for pagination)
              setVideoSwiper(null);
            }

            // Always reinitialize labels carousel
            setLabelsSwiper(null);
            setSwiperReinitKey((k) => k + 1);
          } else {
            // For old design, reinitialize both swipers
            setVideoSwiper(null);
            setLabelsSwiper(null);
            setSwiperReinitKey((k) => k + 1);
          }
          lastBreakpoint = currentBreakpoint;
          lastWidth = currentWidth;
        }
      }, 0);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [redesignToggle]);

  useEffect(() => {
    if (swiperReinitKey === 0) return;
    if (!videoSwiper) return;
    const index = selectedItemIndex ?? 0;
    playItem(items[index], true);
    // No need to slideTo since swiper initializes to correct slide
  }, [swiperReinitKey, videoSwiper, selectedItemIndex]);

  const themeClasses = getThemeClasses(colorTheme, invertColors);

  useEffect(() => {
    playItem(items[selectedItemIndex], inView);
  }, [inView, selectedItemIndex]);

  const playItem = (item: IAppFeatureAnimationItem, play: boolean) => {
    setItemsState((prev) => ({
      ...prev,
      [item.fields?.label]: play
    }));
  };

  const handleSlideChange = (swiper: SwiperClass) => {
    const newIndex = swiper.activeIndex;
    setSelectedItemIndex(newIndex);
    playItem(items[newIndex], true);
  };

  const onMediasSwiperInit = (swiper: SwiperClass) => {
    if (!videoSwiper) {
      setVideoSwiper(swiper);
      const initialIndex = selectedItemIndex ?? 0;
      playItem(items[initialIndex], true);
      swiper.slideTo(initialIndex, 700);
      swiper.on('slideChange', () => handleSlideChange(swiper));
      const swAny = swiper as unknown as SwiperWithPaginationEvents;
      swAny.on('paginationUpdate', () => handleSlideChange(swiper));
      swAny.on('paginationRender', () => handleSlideChange(swiper));
    }
  };

  const onLabelsSwiperInit = (swiper: SwiperClass) => {
    if (!labelsSwiper) {
      setLabelsSwiper(swiper);
    }
  };

  return (
    <>
      {!redesignToggle ? (
        <OldDesign
          items={items}
          ctaWidget={ctaWidget}
          sysId={sysId}
          inViewRef={inViewRef}
          inView={inView}
          itemsState={itemsState}
          videoSwiper={videoSwiper}
          labelsSwiper={labelsSwiper}
          selectedItemIndex={selectedItemIndex}
          onMediasSwiperInit={onMediasSwiperInit}
          onLabelsSwiperInit={onLabelsSwiperInit}
          goToIndex={goToIndex}
          playItem={playItem}
          paginationRef={paginationRef}
        />
      ) : (
        <NewDesign
          items={items}
          sysId={sysId}
          inViewRef={inViewRef}
          inView={inView}
          itemsState={itemsState}
          selectedItemIndex={selectedItemIndex}
          swiperReinitKey={swiperReinitKey}
          themeClasses={themeClasses}
          colorTheme={colorTheme}
          invertColors={invertColors}
          onMediasSwiperInit={onMediasSwiperInit}
          goToIndex={goToIndex}
          playItem={playItem}
          paginationRef={paginationRef}
        />
      )}
    </>
  );
}
